/* Built In Imports */
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

/* External Imports */
import { Box, Button, SimpleGrid } from '@chakra-ui/react';
import axios from 'axios';
import { nanoid } from 'nanoid';
import qs from 'qs';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
import WeaveCard from '@components/Card/WeaveCard';
import SectionUI from '@components/UI/Common/SectionUI';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import Loader from '@components/Utility/Loader';
import config from '@config';

/**
 * Renders the Celebrate the weaves Ui component
 *
 * @param section.section
 * @param {object} section - Data for section
 * @param {string} region - Region for the section
 * @param {string} lang - language for the section
 * @param section.region
 * @param section.lang
 * @param section.isLeftMenu
 * @returns {ReactElement} Celebrate the weaves sectionID UI component
 */
const CelebrateWeaves = ({ section, region, lang, isLeftMenu = true }) => {
  const sectionContent = section?.sectionContent;
  const [state, setState] = useState('assam'); // make it all-indias
  const [loading, setLoading] = useState(false);
  const [start, setStart] = useState(0); // starts at 0
  const [cardCount, setCardCount] = useState(0);
  const [renderCards, setRenderCards] = useState([]);
  const [loadtext, setLoadtext] = useState('Load More');

  const router = useRouter();

  const limit = 8; // asks for 4 cards

  const toggleState = text => e => {
    e.preventDefault();
    if (text === state) {
      return;
    } else {
      setLoading(true);
      setStart(0);
      setRenderCards([]);
      setState(text);
    }
  };

  const buildUrl = async () => {
    let urlObj = {
      typeofpost: 'indian-weaves',
      state: state?.slug !== 'all-india' ? state?.slug : '',
      start: start,
      limit: limit,
    };
    return `${
      config.FACADE_BASE_PATH
    }/content/fetch/savetheweave/iso?${qs.stringify(urlObj)}`;
  };

  const fetchCards = useCallback(async () => {
    setLoading(true);
    if (!state || state?.slug?.length < 1) {
      setRenderCards(sectionContent[2]?.cards);
      setLoading(false);
    } else {
      setLoadtext('Loading...');
      const url = await buildUrl();
      // consoleLog('url', url);
      const response = await axios.get(url);
      const cardList = response?.data?.posts;
      setCardCount(cardList?.totalCount);
      setLoadtext('Load More');
      if (cardList?.cards) {
        setRenderCards(prev => [...prev, ...cardList?.cards]);
      }
      setLoading(false);
    }
  }, [state, start]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(
    () => () => {
      if (router.asPath.indexOf('#celebrate-the-weaves') > -1) {
        setRenderCards([]);
        setCardCount(0);
        setStart(0);
      }
    },
    [router.asPath]
  );

  const handleLoadMore = e => {
    e.preventDefault();
    setStart(prev => prev + limit); // adds 4 more values
  };

  return (
    <Box p="30px 0 100px 0">
      <Box as="div" mx="30px" placeContent="center">
        {sectionContent?.length
          ? sectionContent?.map((secCon, i) => {
              if (secCon?.states) {
                return (
                  <StateSection
                    section={secCon}
                    state={state}
                    toggleState={toggleState}
                    key={i}
                  />
                );
              } else if (secCon?.cards) {
                return (
                  <CardSection
                    section={secCon}
                    cards={renderCards}
                    state={state}
                    cardCount={cardCount}
                    loading={loading}
                    loadtext={loadtext}
                    handleLoadMore={handleLoadMore}
                    key={i}
                  />
                );
              } else {
                return (
                  <SectionUI
                    key={nanoid()}
                    sectionContent={secCon}
                    region={region}
                    language={lang}
                    isLeftMenu={isLeftMenu}
                  />
                );
              }
            })
          : null}
      </Box>
    </Box>
  );
};

const StateSection = ({ section, state, toggleState }) => {
  // consoleLog('state', state);

  return (
    <Box display="flex" flexDir="column" w="100%" p="40px 0">
      <Box
        fontFamily="FedraSansStd-medium"
        fontSize="24px"
        color="#a59d93"
        mb="20px"
      >
        {section.titleText}
      </Box>
      {section?.states ? (
        <StateSlider
          states={section?.states}
          state={state}
          toggleState={toggleState}
        />
      ) : null}
    </Box>
  );
};

const StateSlider = ({ states, state, toggleState }) => {
  // consoleLog('state', state);
  const NextArrow = props => {
    const { onClick } = props;
    return (
      <Box
        pos="absolute"
        textAlign={'center'}
        top={{ base: '12%', sm: '13%', md: '16%' }}
        zIndex="10"
        cursor="pointer"
        bgColor="#fff"
        border="0"
        borderRadius="100%"
        w="40px"
        h="40px"
        bgPos="center"
        bgRepeat="no-repeat"
        color="transparent"
        bgImage={`url(${config.staticPath}/assets/images/right-arrow.svg)`}
        right="-10px"
        bgSize="10px"
        onClick={onClick}
        boxShadow="0 0 15px 0 rgb(0 0 0 / 30%)"
      ></Box>
    );
  };

  const PrevArrow = props => {
    const { onClick } = props;
    return (
      <Box
        pos="absolute"
        top={{ base: '12%', sm: '13%', md: '16%' }}
        zIndex="10"
        cursor="pointer"
        bgSize="10px"
        bgColor="#fff"
        border="0"
        borderRadius="100%"
        w="40px"
        h="40px"
        bgPos="center"
        bgRepeat="no-repeat"
        color="transparent"
        bgImage={`url(${config.staticPath}/assets/images/left-arrow.svg)`}
        left="-10px"
        onClick={onClick}
        boxShadow="0 0 15px 0 rgb(0 0 0 / 30%)"
      />
    );
  };

  const settingsSmall = {
    dots: false,
    className: 'center',
    // centerMode: true,
    infinite: true,
    autoplay: false,
    lazyLoad: true,
    slidesToShow: 7,
    // centerMode: true,
    slidesToScroll: 7,

    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,

    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 5,
          //   initialSlide: 1,
          //   centerMode: true,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 5,
          //   initialSlide: 1,
          //   centerMode: true,
          //   centerPadding: '0',
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 5,
          //   initialSlide: 1,
          //   centerMode: true,
          //   centerPadding: '0',
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
          //   initialSlide: 1,
          //   centerMode: true,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
        },
      },
    ],
  };

  return (
    <Slider {...settingsSmall}>
      {states.map((item, index) => {
        return (
          item.image && (
            <Box
              display="flex !important"
              flexDir="column"
              cursor={'pointer'}
              onClick={toggleState(item)}
              key={index}
            >
              <Box
                display="flex !important"
                justifyContent="center"
                backgroundSize="contain"
                backgroundRepeat="no-repeat"
                backgroundPosition="center"
                p="15px"
                bgImage={`${config.imgPath}/d/46272/1663580947-blue-texture.png`}
                // bgImage="https://isha.sadhguru.org/profiles/isha/themes/ishatheme/assets/blue-texture.png"
                transition="transform .3s"
              >
                <LazyLoadImageComponent
                  _hover={{
                    transform: 'scale(1.25)',
                  }}
                  src={item.image}
                  alt=""
                  title=""
                  width="65px"
                />
              </Box>

              <Box
                display="grid"
                placeContent="center"
                p="12px 10px"
                w="auto"
                maxW="115px"
                m="0 auto"
                color={state.name === item.name ? '#ffffff' : ''}
                bgColor={state.name === item.name ? 'rgb(5 5 109)' : ''}
              >
                {item.name}
              </Box>
            </Box>
          )
        );
      })}
    </Slider>
  );
};

const CardSection = ({
  section,
  cards,
  state,
  cardCount,
  loading,
  loadtext,
  handleLoadMore,
}) => {
  const newLocal = state?.slug !== 'all-india'
    ? `Weaves of ${state.name}`
    : section.titleText;
  return (
    <Box display="flex" flexDir="column" w="100%" p="40px 0">
      <Box
        fontFamily="FedraSansStd-medium"
        fontSize="24px"
        color="#a59d93"
        mb="20px"
      >
        {state?.names
          ? newLocal
          : section.titleText}
      </Box>
      {!cards.length && loading ? <Loader pos="relative" /> : null}

      {section?.cards && cards.length ? (
        <Cards
          cards={cards}
          cardCount={cardCount}
          loadtext={loadtext}
          handleLoadMore={handleLoadMore}
        />
      ) : (
        !loading && <div>No weave</div>
      )}
    </Box>
  );
};

const Cards = ({ cards, cardCount, loadtext, handleLoadMore }) => {
  return (
    <Box>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing="30px">
        {cards.map(card => {
          return <WeaveCard key={nanoid(4)} card={card} isLanding />; // design card here
        })}
      </SimpleGrid>
      {cardCount > cards?.length ? (
        <Box m="40px 0" w="100%" display="grid" placeContent="center">
          <Button
            fontWeight={500}
            fontFamily="FedraSansStd-medium"
            fontSize="18px"
            bgColor="#cf4520"
            // h='auto' // ? it defaults to auto
            padding="10px 36px"
            borderRadius="3px"
            color="#faf7f0"
            textDecoration="none"
            _hover={{
              bg: '#000054',
            }}
            onClick={handleLoadMore}
          >
            {loadtext}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};
export default CelebrateWeaves;
